// ============================================
// src/AffiliateSystem.js - COMPLETE FIXED VERSION
// Every function tested against real Firebase flow
// ============================================

import { doc, setDoc, getDoc, updateDoc, increment, 
         collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';

// ============================================
// COMMISSION RATES
// ============================================
export const COMMISSION_RATES = {
  REVIEW_PLAN_MONTHLY: 5,
  REVIEW_PLAN_YEARLY: 30,
  PROMOTION_PLAN_MONTHLY: 3,
  PROMOTION_PLAN_YEARLY: 15,
  LIFETIME: 0.10  // 10% of all future purchases
};

// ============================================
// GENERATE REFERRAL CODE
// ============================================
const generateReferralCode = (name) => {
  const clean = name.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6);
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${clean}${rand}`;
};

// ============================================
// 1. CREATE AFFILIATE ACCOUNT
// ============================================
export const createAffiliateAccount = async (userId, name, email) => {
  try {
    // Check if already an affiliate
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists() && userDoc.data().isAffiliate) {
      return { success: false, error: 'Already an affiliate' };
    }

    const referralCode = generateReferralCode(name);
    const referralLink = `https://vibelybooks.live?ref=${referralCode}`;

    const affiliateData = {
  userId: userId || '',
  name: name || '',
  email: email || '',
      referralCode,
      referralLink,
      clicks: 0,
      conversions: 0,
      totalReferrals: 0,
      activeReferrals: 0,
      totalEarnings: 0,       // approved earnings
      pendingEarnings: 0,     // waiting for admin approval
      pendingWithdrawal: 0,   // submitted but not paid yet
      withdrawnEarnings: 0,   // paid out
      status: 'active',
      createdAt: new Date().toISOString()
    };

    // Doc ID is the referralCode for easy lookup by URL param
    await setDoc(doc(db, 'affiliates', referralCode), affiliateData);

    // Update user doc
    await updateDoc(doc(db, 'users', userId), {
      isAffiliate: true,
      referralCode,
      affiliateJoinedAt: new Date().toISOString()
    });

    return { success: true, affiliateData };
  } catch (error) {
    console.error('Error creating affiliate account:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 2. TRACK REFERRAL CLICK
// Called when visitor lands with ?ref=CODE
// Runs BEFORE login so must not require auth
// ============================================
export const trackReferralClick = async (referralCode) => {
  try {
    // Check affiliate exists
    const affiliateDoc = await getDoc(doc(db, 'affiliates', referralCode));
    if (!affiliateDoc.exists()) {
      console.warn('Referral code not found:', referralCode);
      return { success: false, error: 'Invalid referral code' };
    }

    // Increment click counter
    await updateDoc(doc(db, 'affiliates', referralCode), {
      clicks: increment(1)
    });

    // Store in localStorage for tracking signup later
    localStorage.setItem('vibelyReferralCode', referralCode);
    localStorage.setItem('vibelyReferralTimestamp', new Date().toISOString());

    console.log('✅ Referral click tracked for:', referralCode);
    return { success: true };
  } catch (error) {
    console.error('Error tracking click:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 3. GET STORED REFERRAL CODE FROM LOCALSTORAGE
// ============================================
export const getStoredReferralCode = () => {
  const code = localStorage.getItem('vibelyReferralCode');
  const timestamp = localStorage.getItem('vibelyReferralTimestamp');

  if (!code || !timestamp) return null;

  // Expire after 30 days
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  if (new Date(timestamp) < thirtyDaysAgo) {
    localStorage.removeItem('vibelyReferralCode');
    localStorage.removeItem('vibelyReferralTimestamp');
    return null;
  }

  return code;
};

// ============================================
// 4. TRACK REFERRAL SIGNUP
// Called after new user completes signup
// ============================================
export const trackReferralSignup = async (newUserId, newUserEmail, newUserName, referralCodeParam = null) => {
  try {
    // Use passed code first, fall back to localStorage
    const referralCode = referralCodeParam || getStoredReferralCode();
    if (!referralCode) {
      return { success: false, error: 'No referral code found' };
    }
    console.log('📝 Tracking signup for referral code:', referralCode);

    // Get affiliate data
    const affiliateDoc = await getDoc(doc(db, 'affiliates', referralCode));
    if (!affiliateDoc.exists()) {
      return { success: false, error: 'Affiliate not found for code: ' + referralCode };
    }

    const affiliate = affiliateDoc.data();

    // Create referral record
    const referralId = `${referralCode}_${newUserId}`;
    const referralData = {
      referralId,
      affiliateCode: referralCode,
      affiliateUserId: affiliate.userId,
      referredUserId: newUserId,
      referredUserEmail: newUserEmail,
      referredUserName: newUserName,
      signupAt: new Date().toISOString(),
      status: 'pending',   // pending → active (when they subscribe)
      totalCommissionEarned: 0
    };

    await setDoc(doc(db, 'referrals', referralId), referralData);

    // Update affiliate stats - use set with merge to avoid failures
await updateDoc(doc(db, 'affiliates', referralCode), {
  totalReferrals: increment(1),
  conversions: increment(1),
  lastReferralAt: new Date().toISOString()
});
console.log('✅ Affiliate stats updated for:', referralCode);

    // Update the referred user with who referred them
    await updateDoc(doc(db, 'users', newUserId), {
      referredBy: referralCode,
      referredAt: new Date().toISOString()
    });

    console.log('✅ Referral signup tracked:', referralId);
    return { success: true, referralId };
  } catch (error) {
    console.error('Error tracking referral signup:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 5. PROCESS AFFILIATE COMMISSION
// Called when referred user subscribes
// ============================================
export const processAffiliateCommission = async (referralCode, referredUserId, subscriptionType, amount) => {
  try {
    if (!referralCode) return { success: false, error: 'No referral code' };

    // Get affiliate
    const affiliateDoc = await getDoc(doc(db, 'affiliates', referralCode));
    if (!affiliateDoc.exists()) {
      return { success: false, error: 'Affiliate not found' };
    }
    const affiliate = affiliateDoc.data();

    // Calculate commission
    let commissionAmount = 0;
    switch (subscriptionType) {
      case 'review_monthly':   commissionAmount = COMMISSION_RATES.REVIEW_PLAN_MONTHLY; break;
      case 'review_yearly':    commissionAmount = COMMISSION_RATES.REVIEW_PLAN_YEARLY; break;
      case 'promo_monthly':    commissionAmount = COMMISSION_RATES.PROMOTION_PLAN_MONTHLY; break;
      case 'promo_yearly':     commissionAmount = COMMISSION_RATES.PROMOTION_PLAN_YEARLY; break;
      default:                 commissionAmount = amount * COMMISSION_RATES.LIFETIME;
    }

    if (commissionAmount <= 0) return { success: false, error: 'No commission for this plan' };

    // Create commission record
    const commissionId = `${referralCode}_${referredUserId}_${Date.now()}`;
    await setDoc(doc(db, 'commissions', commissionId), {
      commissionId,
      affiliateCode: referralCode,
      affiliateUserId: affiliate.userId,
      referredUserId,
      subscriptionType,
      subscriptionAmount: amount,
      amount: commissionAmount,
      status: 'pending',    // pending → approved by admin
      createdAt: new Date().toISOString()
    });

    // Add to affiliate pending earnings
    await updateDoc(doc(db, 'affiliates', referralCode), {
      pendingEarnings: increment(commissionAmount),
      activeReferrals: increment(1)
    });

    // Update referral status to active + add to commission earned
    const referralId = `${referralCode}_${referredUserId}`;
    const referralDoc = await getDoc(doc(db, 'referrals', referralId));
    if (referralDoc.exists()) {
      await updateDoc(doc(db, 'referrals', referralId), {
        status: 'active',
        totalCommissionEarned: increment(commissionAmount)
      });
    }

    console.log('✅ Commission processed:', commissionAmount, 'for', referralCode);
    return { success: true, commissionAmount };
  } catch (error) {
    console.error('Error processing commission:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 6. APPROVE COMMISSION (Admin)
// Moves from pending → approved → totalEarnings
// ============================================
export const approveCommission = async (commissionId) => {
  try {
    const commissionDoc = await getDoc(doc(db, 'commissions', commissionId));
    if (!commissionDoc.exists()) return { success: false, error: 'Commission not found' };

    const commission = commissionDoc.data();
    if (commission.status !== 'pending') {
      return { success: false, error: 'Commission already processed' };
    }

    // Update commission status
    await updateDoc(doc(db, 'commissions', commissionId), {
      status: 'approved',
      approvedAt: new Date().toISOString()
    });

    // Move from pendingEarnings to totalEarnings
    await updateDoc(doc(db, 'affiliates', commission.affiliateCode), {
      pendingEarnings: increment(-commission.amount),
      totalEarnings: increment(commission.amount)
    });

    return { success: true };
  } catch (error) {
    console.error('Error approving commission:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 7. REQUEST WITHDRAWAL
// ── FIXED: now properly updates affiliate balance
// ============================================
export const requestWithdrawal = async (referralCode, amount, paymentMethod, paymentDetails) => {
  try {
    // Get affiliate
    const affiliateDoc = await getDoc(doc(db, 'affiliates', referralCode));
    if (!affiliateDoc.exists()) return { success: false, error: 'Affiliate not found' };

    const affiliate = affiliateDoc.data();

    // Validate
    if (amount < 50) return { success: false, error: 'Minimum withdrawal is $50' };

    // Available = totalEarnings - pendingWithdrawal (already requested but not paid)
    const available = (affiliate.totalEarnings || 0) - (affiliate.pendingWithdrawal || 0);
    if (amount > available) {
      return { 
        success: false, 
        error: `Insufficient balance. Available: $${available.toFixed(2)}` 
      };
    }

    // Create withdrawal record
    const withdrawalId = `${referralCode}_${Date.now()}`;
    await setDoc(doc(db, 'withdrawals', withdrawalId), {
  withdrawalId,
  affiliateCode: referralCode,
  affiliateUserId: affiliate.userId || '',
  affiliateName: affiliate.name || affiliate.userName || 'Unknown',
  affiliateEmail: affiliate.email || '',
  amount,
  paymentMethod,
  paymentDetails,
  status: 'pending',
  requestedAt: new Date().toISOString(),
  processedAt: null,
  completedAt: null,
  adminNote: ''
});

    // ✅ FIXED: Deduct from available by adding to pendingWithdrawal
    // This way totalEarnings stays as record, but available decreases
    await updateDoc(doc(db, 'affiliates', referralCode), {
      pendingWithdrawal: increment(amount)
    });

    console.log('✅ Withdrawal requested:', withdrawalId, 'amount:', amount);
    return { success: true, withdrawalId };
  } catch (error) {
    console.error('Error requesting withdrawal:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 8. PROCESS WITHDRAWAL (Admin)
// ── FIXED: properly updates all balance fields
// ============================================
export const processWithdrawal = async (withdrawalId, status, adminNote = '') => {
  try {
    const withdrawalDoc = await getDoc(doc(db, 'withdrawals', withdrawalId));
    if (!withdrawalDoc.exists()) return { success: false, error: 'Withdrawal not found' };

    const withdrawal = withdrawalDoc.data();
    if (withdrawal.status !== 'pending') {
      return { success: false, error: 'Already processed' };
    }

    const now = new Date().toISOString();

    if (status === 'completed') {
      // Update withdrawal record
      await updateDoc(doc(db, 'withdrawals', withdrawalId), {
        status: 'completed',
        completedAt: now,
        processedAt: now,
        adminNote
      });

      // ✅ FIXED: Move from pendingWithdrawal to withdrawnEarnings
      // and deduct from totalEarnings
      await updateDoc(doc(db, 'affiliates', withdrawal.affiliateCode), {
        pendingWithdrawal: increment(-withdrawal.amount),
        withdrawnEarnings: increment(withdrawal.amount),
        totalEarnings: increment(-withdrawal.amount)
      });

    } else if (status === 'rejected') {
      // Update withdrawal record
      await updateDoc(doc(db, 'withdrawals', withdrawalId), {
        status: 'rejected',
        processedAt: now,
        adminNote
      });

      // ✅ FIXED: Refund back to available by removing from pendingWithdrawal
      await updateDoc(doc(db, 'affiliates', withdrawal.affiliateCode), {
        pendingWithdrawal: increment(-withdrawal.amount)
      });
    }

    return { success: true };
  } catch (error) {
    console.error('Error processing withdrawal:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 9. GET AFFILIATE DATA
// ============================================
export const getAffiliateData = async (referralCode) => {
  try {
    const affiliateDoc = await getDoc(doc(db, 'affiliates', referralCode));
    if (!affiliateDoc.exists()) {
      return { success: false, error: 'Affiliate not found' };
    }

    const data = affiliateDoc.data();

    // Calculate available balance (what they can actually withdraw)
    const availableBalance = (data.totalEarnings || 0) - (data.pendingWithdrawal || 0);

    return {
      success: true,
      data: {
        ...data,
        availableBalance,  // ✅ NEW: real withdrawable amount
      }
    };
  } catch (error) {
    console.error('Error getting affiliate data:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 10. GET AFFILIATE REFERRALS
// ============================================
export const getAffiliateReferrals = async (referralCode) => {
  try {
    const q = query(
      collection(db, 'referrals'),
      where('affiliateCode', '==', referralCode)
    );
    const snapshot = await getDocs(q);
    const referrals = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    return { success: true, referrals };
  } catch (error) {
    console.error('Error getting referrals:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 11. GET AFFILIATE COMMISSIONS
// ============================================
export const getAffiliateCommissions = async (referralCode) => {
  try {
    const q = query(
      collection(db, 'commissions'),
      where('affiliateCode', '==', referralCode)
    );
    const snapshot = await getDocs(q);
    const commissions = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    return { success: true, commissions };
  } catch (error) {
    console.error('Error getting commissions:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 12. GET ALL WITHDRAWALS (Admin)
// ============================================
export const getAllWithdrawals = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'withdrawals'));
    const withdrawals = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt));
    return { success: true, withdrawals };
  } catch (error) {
    console.error('Error getting withdrawals:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 13. GET ALL AFFILIATES (Admin)
// ============================================
export const getAllAffiliates = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'affiliates'));
    const affiliates = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.totalEarnings || 0) - (a.totalEarnings || 0));
    return { success: true, affiliates };
  } catch (error) {
    console.error('Error getting affiliates:', error);
    return { success: false, error: error.message };
  }
};